import Add from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import React, { ChangeEvent, useState } from 'react';

import { StructureChildProps } from './StructureChild.props';
import { TableWrapper } from './StructureChild.styles';
import { ActionPermissions, ColorProps, SizeProps } from '../../../@types';
import { GridFeatureModeConstant } from '../../../configs/enums';
import { v4PanelToggleSx, v4QuietButtonSx } from '../../atoms/Button';
import { DataGrid } from '../../atoms/DataGrid';
import { FormControlLabel } from '../../atoms/FormControlLabel';
import { Panel } from '../../atoms/Panel';
import { PermissionLink } from '../../atoms/PermissionLink';
import { Switch } from '../../atoms/Switch';
import { TablePill } from '../../atoms/TablePill';

/** Matches the address and contact row actions — muted until pointed at. */
const ROW_ICON_SX = { color: 'text.secondary', '&:hover': { color: 'v4.accentDark' } };

export const StructureChild: React.FC<StructureChildProps> = ({
  switchLabel = 'Active',
  childrenData,
  hasViewChildrenEditPermission = true,
  onClick,
  onChangeActiveFlag,
  parentStructureType,
  isClientCustomerStructureCreation,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const sortedChildrenData = [...childrenData].sort((a, b) => {
    return (b.isActive === true ? 1 : 0) - (a.isActive === true ? 1 : 0);
  });

  const columns: GridColDef[] = [
    {
      field: 'typeName',
      headerName: 'Node Type',
      flex: 0.3,
      // The same pill the Structures list gives a node type — see
      // `StructureTableRow`.
      renderCell: (params: GridRenderCellParams) =>
        params.row.typeName ? <TablePill>{params.row.typeName}</TablePill> : null,
    },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (params: GridRenderCellParams) =>
        params.row.id ? (
          <PermissionLink
            permission={ActionPermissions.Structure_Edit_View_Structure_Location}
            href={`/structure/${params.row.id}`}
          >
            {params.row.name}
          </PermissionLink>
        ) : (
          params.row.name
        ),
      flex: 0.4,
    },
    {
      field: 'id',
      headerName: 'Action',
      sortable: false,
      width: 220,
      headerAlign: 'center',
      // Sized to the rest of the page: 18px icons in small buttons, matching the
      // address and contact rows, and the panel-header toggle treatment for the
      // switch. They were default-size MUI buttons with 24px icons and a 14px
      // switch label, which made one table cell the loudest thing on the tab.
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            width: '100%',
          }}
        >
          {hasViewChildrenEditPermission && (
            <>
              <IconButton
                aria-label={`Add a child under ${params.row.name}`}
                size="small"
                onClick={() => onClick && onClick(params.row.type)}
                sx={ROW_ICON_SX}
              >
                <Add sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                aria-label={`Edit ${params.row.name}`}
                size="small"
                onClick={() => onClick && onClick(undefined, params.row.id)}
                sx={ROW_ICON_SX}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
          <FormControlLabel
            sx={{ ...v4PanelToggleSx, ml: 0.5 }}
            control={
              <Switch
                disabled={!hasViewChildrenEditPermission}
                size={SizeProps.Small}
                // Primary, matching the Active toggle on Details and the
                // include-child-records switches on this same tab.
                color={ColorProps.Primary}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChangeActiveFlag(e.target.checked, params.row.id)
                }
                checked={params.row.isActive}
              />
            }
            label={switchLabel}
          />
        </Box>
      ),
    },
  ];

  return (
    <Panel
      title="Children"
      titleAdornment={<TablePill tone="neutral">{childrenData.length}</TablePill>}
      collapsible
      bodyPadding={0}
      actions={
        hasViewChildrenEditPermission && (
          <MuiButton
            startIcon={<Add />}
            sx={v4QuietButtonSx}
            onClick={() =>
              onClick && onClick(isClientCustomerStructureCreation ? parentStructureType : '')
            }
          >
            Add child
          </MuiButton>
        )
      }
    >
      <TableWrapper>
        <DataGrid
          v4Skin
          columns={columns}
          rows={sortedChildrenData}
          rowCount={sortedChildrenData.length}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableDensitySelector={true}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          paginationMode={GridFeatureModeConstant.client}
          getRowClassName={(params) => (!params.row.isActive ? 'gray-out-row' : '')}
          rowsPerPageOptions={[15, 30, 100]}
          pagination
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
            },
          }}
        />
      </TableWrapper>
    </Panel>
  );
};
